import React from 'react'
import { Feature } from '../api/client'
import { reconstructDataFromNormalized } from '../utils/dataNormalization'
import ChatCheckinView from './ChatCheckinView'
import { emptyParseActivity, type ParseActivity } from '../hooks/useParseActivity'
import CollapsingCardList from './CollapsingCardList'

type AvailableAction = 'edit' | 'duplicate' | 'delete' | 'reanalyze' | 'edit-in-page' | 'discuss'

interface CheckinSetViewProps {
    /**
     * Normalized payload (checkins + features + projects + collections + collection_items),
     * as produced by `fetch_checkins_by_ids` on the backend and emitted alongside
     * Genie messages such as `selected_checkin_list` and `list_checkins`.
     */
    data: any
    features: Feature[]
    formatDate: (dateString: string, includeYearIfDifferent?: boolean) => string
    /** Italic lead-in (e.g. "Found 3 checkins from last week:"). Rendered above the set. */
    lead?: React.ReactNode
    availableActions?: AvailableAction[]
    /** If set and the set is larger, only this many checkins render initially, with a Show more/less toggle. */
    initialLimit?: number
    /**
     * Hide each card's snapshot/live toggle. Set for checkins this session just
     * wrote, where the snapshot is current by construction.
     */
    suppressToggle?: boolean
    /** Checkins whose parse is still running; those cards show the analyzing state. */
    analyzingCheckinIds?: Set<number>
    /**
     * What each of those parses is actually doing, keyed by checkin id. Genie
     * drives its created checkins' parses as watched jobs like the
     * composer does, so a card here gets the same live panel. A checkin with no
     * record — or one whose record already reported an outcome — falls back to
     * the stepless indicator below.
     */
    activities?: Record<number, ParseActivity>
    /** Expand / collapse one card's parse panel. Absent leaves them collapsed
     *  and chevron-less. */
    onToggleParseExpanded?: (checkinId: number, expanded: boolean) => void
    onToggleParseTechnical?: (checkinId: number, technical: boolean) => void
    onRefresh?: () => void
}

/**
 * The stand-in for a parse that has not said anything yet, or whose record has
 * already reported its outcome. A stepless record renders exactly the indicator
 * this surface has always shown: the indeterminate comb, "Analyzing...", and no
 * chevron, because there is nothing to expand into. Module-level so it keeps one
 * identity across renders.
 */
const ANALYZING_ONLY: ParseActivity = emptyParseActivity(0)

/**
 * The record to draw for a checkin whose indicator is up. A record that has
 * already reported its outcome describes a parse that is over, which is not
 * what this indicator is about — the same rule `CheckinsPage.activityFor`
 * keeps, and the reason both fall back to the stepless stand-in.
 */
const activityFor = (
    activities: Record<number, ParseActivity> | undefined,
    checkinId: number,
): ParseActivity => {
    const activity = activities?.[checkinId]
    return activity && activity.status === 'running' ? activity : ANALYZING_ONLY
}

/**
 * Shared renderer for a set of checkins that lands in the Genie chat — both
 * lists the AI returned (`list_checkins` review/display) and sets the user
 * pulled in from the checkins page (`selected_checkin_list`). Wraps each
 * checkin in `ChatCheckinView` for the snapshot/live toggle.
 */
const CheckinSetView: React.FC<CheckinSetViewProps> = ({
    data,
    features,
    formatDate,
    lead,
    availableActions = ['edit-in-page'],
    initialLimit,
    suppressToggle = false,
    analyzingCheckinIds,
    activities,
    onToggleParseExpanded,
    onToggleParseTechnical,
    onRefresh,
}) => {
    const { checkins, collectionItems } = reconstructDataFromNormalized(data)

    // One open peek across the whole set — the list owns it, so reading a second
    // body closes the first rather than stacking modals.
    const [openCheckinId, setOpenCheckinId] = React.useState<number | null>(null)

    return (
        <div className="mb-4">
            {lead}
            <CollapsingCardList
                items={checkins}
                initialLimit={initialLimit}
                className="space-y-4"
                renderItem={(checkin: any, checkinIndex: number) => (
                    <ChatCheckinView
                        key={checkin.id}
                        snapshotCheckin={checkin}
                        snapshotCollectionItems={collectionItems}
                        suppressToggle={suppressToggle}
                        onPeek={() => setOpenCheckinId(checkin.id)}
                        peekOpen={openCheckinId === checkin.id}
                        onPeekClose={() => setOpenCheckinId(null)}
                        index={checkinIndex}
                        showSeparator={checkinIndex > 0}
                        editingCheckinId={null}
                        showNewCheckinComposer={false}
                        features={features}
                        recentFeatures={[]}
                        sortOrder="most_recent"
                        savingState={null}
                        activity={
                            analyzingCheckinIds?.has(checkin.id)
                                ? activityFor(activities, checkin.id)
                                : undefined
                        }
                        onToggleParseTechnical={
                            onToggleParseTechnical
                                ? (technical: boolean) =>
                                      onToggleParseTechnical(checkin.id, technical)
                                : undefined
                        }
                        onToggleParseExpanded={
                            onToggleParseExpanded
                                ? (expanded: boolean) => onToggleParseExpanded(checkin.id, expanded)
                                : undefined
                        }
                        showMenu={true}
                        availableActions={availableActions}
                        formatDate={formatDate}
                        getComposerCheckinFromCheckin={() => ({ body: '', applies_to: '', features: [], projects: [] })}
                        handleFieldEdit={() => {}}
                        handleDeleteCheckin={() => {}}
                        handleEditCheckin={() => {}}
                        handleDuplicateCheckin={() => {}}
                        handleFilterSelect={() => {}}
                        handleReanalyzeCheckin={() => {}}
                        onRefresh={onRefresh}
                        setEditingCheckinId={() => {}}
                        setCheckins={() => {}}
                        setShowNewCheckinComposer={() => {}}
                        firstElementIsEmptyCheckin={() => false}
                        isSelectionMode={false}
                        isSelected={false}
                        onToggleSelection={() => {}}
                    />
                )}
            />
        </div>
    )
}

export default CheckinSetView
